import { ViewController } from 'elc-service';
import { computed, observable, action } from 'mobx';
import { serviceNames, diContainer } from '../../service-setup/diContainer';
import { root } from 'elc-universal-utils';
import { events, emit } from 'elc-service-bus';
import { IAsset } from '../domain/entities/Asset';
import { IImageDevice, IImagesSizes } from '../interfaces/IProdcat';
import { ComponentTypes } from '../constants/ComponentTypes';
import { AssetTypes } from '../constants/AssetTypes';
import { DeviceType, DESKTOP, MOBILE } from '../constants/DeviceTypes';
import { Product } from '../domain/entities/Product';
import { Sku } from '../domain/entities/Sku';

export interface IImagesObject {
    images: IAsset[];
    defaultImage: IAsset;
}

export interface IImagesForDevice {
    [key: string]: IImagesObject;
}

export class ProductBaseController extends ViewController {
    public static shadeUrlParam = 'shade';
    public static sizeUrlParam = 'size';

    @observable product: Product;
    @observable selectedSkuId: string;
    @observable public selectedAutoReplenishOption: number = 0;
    @observable public isAutoReplenishSelected = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @observable private prodcatConfig: any;

    constructor() {
        super();
        this.prodcatConfig = diContainer.get(serviceNames.config);
    }

    @action
    public setProduct(product: Product) {
        this.product = product;
    }

    @action
    public setSelectedSkuId(skuId: string) {
        this.selectedSkuId = skuId;
    }

    @computed
    public get imagesSizeConfig() {
        const defaultImagesSizesConfig: IImagesSizes = {
            productGrid: {
                desktop: AssetTypes.MEDIUM,
                mobile: AssetTypes.MEDIUM
            },
            productQuickshop: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            },
            productFull: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            }
        };

        return this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imagesSizes
            ? this.prodcatConfig.config.imagesSizes
            : defaultImagesSizesConfig;
    }

    @computed
    public get selectedSku(): Sku {
        return (
            this.product.skus.find(sku => sku.skuId === this.selectedSkuId) ||
            this.product.defaultSku
        );
    }

    @computed
    get largeImages() {
        let skuImages: IAsset[] = [];
        let productLargeImages: IAsset[] = [];
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.hasLargeImages) {
                [...skuImages] = selectedSku.largeImages;
            }
        }

        if (product && product.hasLargeImages) {
            [...productLargeImages] = product.largeImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productLargeImages]);
    }

    @computed
    get mediumImages() {
        let skuImages: IAsset[] = [];
        let productMediumImages: IAsset[] = [];
        const { product } = this;
        const selectedSku = this.selectedSku;

        if (selectedSku) {
            if (selectedSku.hasMediumImages) {
                [...skuImages] = selectedSku.mediumImages;
            }
        }

        if (product && product.hasMediumImages) {
            [...productMediumImages] = product.mediumImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productMediumImages]);
    }

    @computed
    public get altTextPrefix(): string {
        return this.selectedSku.selectedShade.name || this.selectedSku.selectedSize.value || '';
    }

    @computed
    public get imageCarouselSettings() {
        return (
            this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imageCarouselSettings
        );
    }

    private getImagesForDevice(device: DeviceType, imagesSizeConfig?: IImageDevice): IImagesObject {
        const useLargeImages = imagesSizeConfig && imagesSizeConfig[device] === AssetTypes.LARGE;
        const selectedSku = this.selectedSku;

        const images = useLargeImages ? this.largeImages : this.mediumImages;
        const defaultImage = useLargeImages
            ? this.enhanceImageAltText(selectedSku.defaultLargeImage)
            : this.enhanceImageAltText(selectedSku.defaultMediumImage);

        return {
            images,
            defaultImage
        };
    }

    public getCustomSizeImages(component: ComponentTypes): IImagesForDevice {
        const imagesSizeConfig = this.imagesSizeConfig && this.imagesSizeConfig[component];

        return {
            desktop: this.getImagesForDevice(DESKTOP, imagesSizeConfig),
            mobile: this.getImagesForDevice(MOBILE, imagesSizeConfig)
        };
    }

    @action
    public setAutoReplenishOption = (option: number) => {
        this.selectedAutoReplenishOption = option;
    };

    @action
    public setAutoReplenishSelection = (isSelected: boolean) => {
        this.isAutoReplenishSelected = isSelected;
        if (!isSelected) {
            this.setAutoReplenishOption(0);
        }
    };

    @action
    public toggleAutoReplenishSelection = () => {
        this.isAutoReplenishSelected = !this.isAutoReplenishSelected;
    };

    @action
    public selectShade = (name: string) => {
        const clickedShade = this.product.getShadeByName(name);
        if (clickedShade && clickedShade.parentSku) {
            this.selectedSkuId = clickedShade.parentSku.skuId;
        }
    };

    @action
    public selectSize = (label: string) => {
        const clickedSize = this.product.getSizeByLabel(label);
        if (clickedSize && clickedSize.parentSku) {
            this.selectedSkuId = clickedSize.parentSku.skuId;
        }
    };

    public sanitizeSelection = (selection: string) => {
        return selection
            .replace(/\s/g, ' ')
            .split(' ')
            .join('_');
    };

    public urlUpdateState = (searchKey: string, searchValue: string) => {
        const search = (<Location>root.location).search;
        const pathName = (<Location>root.location).pathname;
        const urlParams = new URLSearchParams(search);
        const sanitizedValue = this.sanitizeSelection(searchValue);
        urlParams.set(searchKey, sanitizedValue);
        window.history.replaceState({}, '', `${pathName}?${urlParams.toString()}`);
    };

    public clearURLState = (searchKey: string) => {
        const search = (<Location>root.location).search;
        const urlParams = new URLSearchParams(search);
        urlParams.delete(searchKey);
        let urlNewState = `${window.location.pathname}`;
        const queryParams = urlParams.toString();
        if (queryParams !== '') {
            urlNewState = urlNewState.concat(`?${queryParams}`);
        }
        window.history.replaceState({}, '', `${urlNewState}`);
    };

    public handleShadeSelect = (shadeName: string) => {
        this.selectShade(shadeName);

        const {
            selectedShade: { name },
            skuId
        } = this.selectedSku;

        emit(events.PRODUCT_SHADE_SELECTED, { skuId });

        if (name) {
            this.urlUpdateState(ProductBaseController.shadeUrlParam, name);
        } else {
            this.clearURLState(ProductBaseController.shadeUrlParam);
        }
    };

    public handleSizeSelect = (label: string) => {
        this.selectSize(label);
        const { value } = this.selectedSku.selectedSize;
        if (value) {
            this.urlUpdateState(ProductBaseController.sizeUrlParam, value);
        } else {
            this.clearURLState(ProductBaseController.sizeUrlParam);
        }
    };

    public handleFavoriteClick = () => {
        emit(events.FAVORITE_SELECTED, {
            skuBaseId: this.selectedSku.perlgem.SKU_BASE_ID
        });
    };

    private enhanceImageListAltText(images: IAsset[]) {
        let skuImages: IAsset[] = [];
        skuImages = images.map((image, index) => {
            const isFirstImage = !index;
            if (isFirstImage) {
                return this.enhanceImageAltText(image);
            }

            const img = { ...image };
            img.alt = '';

            return img;
        });

        return skuImages;
    }

    private enhanceImageAltText(image: IAsset) {
        const img = { ...image };
        img.alt = `${this.altTextPrefix} | ${img.alt}`;

        return img;
    }

    @computed
    get selectedProductUrl() {
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.selectedShade && selectedSku.selectedShade.name) {
                return `${product.productUrl}?${
                    ProductBaseController.shadeUrlParam
                }=${encodeURIComponent(selectedSku.selectedShade.name)}`;
            }
            if (selectedSku.selectedSize && selectedSku.selectedSize.value) {
                return `${product.productUrl}?${
                    ProductBaseController.sizeUrlParam
                }=${encodeURIComponent(selectedSku.selectedSize.value)}`;
            }
        }

        return product.productUrl;
    }
}
